import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { Button } from './ui/button';
import { 
  Activity, 
  LayoutDashboard, 
  User, 
  Zap, 
  History, 
  TrendingUp, 
  LogOut, 
  Menu, 
  X,
  Timer,
  Bike,
  Bot,
  Crown,
  Bell,
  ShieldCheck,
  Dumbbell
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const logoUrl = "https://customer-assets.emergentagent.com/job_ai-gym-guide/artifacts/gwvje8fa_IMG_20220211_145748_413%20%285%29.jpg";

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/assistant', label: t('assistant'), icon: Bot },
    { path: '/profile', label: t('profile'), icon: User },
    { path: '/generate', label: t('generatePlan'), icon: Zap },
    { path: '/exercises', label: t('exercises'), icon: Dumbbell },
    { path: '/history', label: t('history'), icon: History },
    { path: '/progression', label: t('progression'), icon: TrendingUp },
    { path: '/timer', label: t('timer'), icon: Timer },
    { path: '/strava', label: t('strava'), icon: Bike },
    { path: '/notifications', label: t('reminders'), icon: Bell },
    { path: '/pricing', label: t('plans'), icon: Crown },
  ];
  
  // Add admin link if user is admin
  if (isAdmin) {
    navItems.push({ path: '/admin', label: t('admin'), icon: ShieldCheck, isAdmin: true });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 border-r border-white/10 bg-[#0a0a0a] z-40">
        <div className="p-6 border-b border-white/10 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src={logoUrl} alt="Process Body" className="w-10 h-10 rounded-full object-cover" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              PROCESS<span className="text-[#32CD32]">BODY</span>
            </span>
          </Link>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto min-h-0">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <Link 
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isActive(item.path)
                        ? item.isAdmin 
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-[#32CD32]/10 text-[#32CD32]'
                        : item.isAdmin
                          ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                          : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          
          {/* User section inside scroll area */}
          <div className="mt-6 pt-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#32CD32]/10 flex items-center justify-center">
                <span className="text-[#32CD32] font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="mb-2">
              <LanguageSwitcher variant="sidebar" />
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-400/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              {t('logout')}
            </Button>
          </div>
        </nav>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 border-b border-white/10 bg-[#0a0a0a] z-40 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
          <img src={logoUrl} alt="Process Body" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
          <span className="text-lg font-bold tracking-tight truncate" style={{ fontFamily: 'Barlow Condensed' }}>
            PROCESS<span className="text-[#32CD32]">BODY</span>
          </span>
        </Link>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[#0a0a0a] pt-16 overflow-y-auto">
          <nav className="p-4">
            <ul className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link 
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-4 rounded-xl transition-colors ${
                        isActive(item.path)
                          ? item.isAdmin
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-[#32CD32]/10 text-[#32CD32]'
                          : item.isAdmin
                            ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="font-medium text-lg">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div className="mt-8 pt-8 border-t border-white/10">
              <div className="flex items-center gap-3 px-4 py-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-[#32CD32]/10 flex items-center justify-center">
                  <span className="text-[#32CD32] font-bold text-lg">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
              <div className="mb-2">
                <LanguageSwitcher variant="sidebar" />
              </div>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-red-400 hover:bg-red-400/10 py-4"
                onClick={handleLogout}
              >
                <LogOut className="w-6 h-6 mr-3" />
                {t('logout')}
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 md:p-8 lg:p-12 pb-28 lg:pb-12">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
